import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { useKonaseemaGuide } from "@/hooks/usePublicGuide";
import { useTripPlan } from "@/hooks/useTripPlan";
import { buildItineraryText, formatRupees, summarizeItinerary } from "@/pages/plan-trip/planUtils";

type Status = "idle" | "loading" | "success" | "error";

const SUBMIT_URL = "https://readdy.ai/api/form/dalu2gth599pccsgtuk0";

const topics = [
  "General enquiry",
  "Plan a family visit",
  "Plan a school visit",
  "Plan a corporate experience",
  "Farm partnership",
  "Media & press",
  "Something else",
];

export default function ContactFormSection() {
  const { mapEmbed } = useKonaseemaGuide();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const { plan, dayCount, totalStops } = useTripPlan();
  const tripSummary = useMemo(() => summarizeItinerary(plan, dayCount), [plan, dayCount]);
  const tripText = useMemo(() => buildItineraryText(plan, dayCount), [plan, dayCount]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you! Your message has reached us. We'll reply shortly.");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("Thank you! Your message has reached us. We'll reply within 24 hours.");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "Something went wrong. Please try again.",
        );
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please check your connection and try again.");
    }
  };

  const inputClass =
    "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

  return (
    <section id="contact-form" className="sun-glaze bg-secondary-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          <Reveal className="lg:col-span-6">
            <div className="h-full bg-background-50 rounded-lg border border-secondary-200 p-7 md:p-9">
              <div className="flex items-center gap-3 mb-5">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
                <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                  Send a Message
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
                Tell Us What You're Planning
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Share a few details and our team will get back with availability, a clear quote and
                a suggested plan.
              </p>

              <form
                id="rythugaru-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-7 space-y-4"
              >
                {totalStops > 0 && (
                  <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                        <i className="ri-route-line text-lg"></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-accent-700">
                          Your Trip rides along with this message
                        </p>
                        <p className="mt-1.5 text-sm font-semibold text-foreground-900">
                          {tripSummary.stops} {tripSummary.stops === 1 ? "stop" : "stops"} ·{" "}
                          {tripSummary.days} {tripSummary.days === 1 ? "day" : "days"} ·{" "}
                          {tripSummary.hours} hrs · {formatRupees(tripSummary.cost)}
                        </p>
                        <p className="text-xs text-foreground-600 mt-1.5 leading-relaxed">
                          We'll attach your saved day-by-day itinerary to this enquiry automatically.
                        </p>
                        <Link
                          to="/plan-your-trip"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 hover:text-accent-800 transition-colors cursor-pointer"
                        >
                          Review or edit your plan
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
                {totalStops > 0 && (
                  <input type="hidden" name="trip_plan" value={tripText} readOnly />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ct-name" className={labelClass}>
                      Your name
                    </label>
                    <input
                      id="ct-name"
                      name="name"
                      type="text"
                      required
                      placeholder="e.g. Ananya Sharma"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="ct-phone" className={labelClass}>
                      Phone
                    </label>
                    <input
                      id="ct-phone"
                      name="phone"
                      type="tel"
                      placeholder="+91 ..."
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="ct-email" className={labelClass}>
                    Email
                  </label>
                  <input
                    id="ct-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ct-topic" className={labelClass}>
                      What's this about?
                    </label>
                    <select id="ct-topic" name="topic" className={`${inputClass} cursor-pointer`}>
                      {topics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ct-group" className={labelClass}>
                      Group size
                    </label>
                    <input
                      id="ct-group"
                      name="group_size"
                      type="number"
                      min="1"
                      placeholder="e.g. 4"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="ct-message" className={labelClass}>
                    Your message
                  </label>
                  <textarea
                    id="ct-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your group, preferred dates, interests or any special requests."
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                <div className="form-extra-contact" aria-hidden="true">
                  <label htmlFor="ct-website-alt">Leave this field empty</label>
                  <input
                    id="ct-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? "Sending..." : "Send Message"}
                  {status !== "loading" && <i className="ri-send-plane-fill"></i>}
                </button>

                {status === "success" && (
                  <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-100 border border-accent-300 rounded-md px-4 py-3">
                    <i className="ri-error-warning-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
              </form>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={120}>
            <div className="h-full flex flex-col gap-6">
              <div className="flex-1 min-h-[20rem] rounded-lg overflow-hidden border border-secondary-200 bg-background-100">
                <iframe
                  title="Rythugaru location map — Konaseema, Andhra Pradesh"
                  src={mapEmbed}
                  className="w-full h-full min-h-[20rem]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                ></iframe>
              </div>

              <div className="bg-primary-900 rounded-lg p-7 md:p-8">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-time-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-background-50 font-semibold mt-5">
                  How Fast We Reply
                </h3>
                <div className="mt-6 space-y-4">
                  {[
                    { label: "WhatsApp", value: "Usually under 30 minutes" },
                    { label: "Phone call", value: "8 AM – 8 PM IST, every day" },
                    { label: "Email", value: "Within 24 hours" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between gap-4 border-b border-primary-800 pb-3 last:border-0 last:pb-0"
                    >
                      <span className="text-sm font-medium text-background-100">{item.label}</span>
                      <span className="text-sm text-accent-300 font-semibold text-right">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}